import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { ui, useLang } from "../i18n/i18n";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t, lang, setLang } = useLang();

  const navItems = [
    { to: "/", label: t(ui.nav.home), idx: "01", key: "home" },
    { to: "/about", label: t(ui.nav.about), idx: "02", key: "about" },
    { to: "/products", label: t(ui.nav.products), idx: "03", key: "products" },
    { to: "/contact", label: t(ui.nav.contact), idx: "04", key: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const LangSwitch = ({ testIdPrefix = "" }) => (
    <div
      className="inline-flex items-center border border-[#E5E7EB]"
      data-testid={`${testIdPrefix}lang-switcher`}
    >
      {["en", "az"].map((code) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          data-testid={`${testIdPrefix}lang-${code}`}
          aria-pressed={lang === code}
          className={`mono text-[11px] tracking-[0.18em] uppercase px-2.5 py-1.5 transition-colors ${
            lang === code
              ? "bg-[#0B1F3A] text-white"
              : "text-[#6B7280] hover:text-[#0B1F3A]"
          }`}
        >
          {code}
        </button>
      ))}
    </div>
  );

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b transition-all ${
        scrolled ? "border-[#E5E7EB]" : "border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <div className="w-8 h-8 border border-[#0B1F3A] flex items-center justify-center">
              <div className="w-2 h-2 bg-[#0B1F3A] group-hover:rotate-45 transition-transform" />
            </div>
            <div className="leading-tight">
              <div className="text-[15px] font-semibold tracking-tight text-[#0B1F3A]">
                {t(ui.brand)}
              </div>
              <div className="mono text-[10px] tracking-[0.2em] uppercase text-[#6B7280]">
                {t(ui.tagline)}
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.key}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                    isActive ? "text-[#0B1F3A]" : "text-[#4B5563] hover:text-[#0B1F3A]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="mono text-[10px] text-[#9CA3AF]">{item.idx}</span>
                    <span>{item.label}</span>
                    {isActive && (
                      <span className="absolute left-4 right-4 -bottom-[1px] h-[2px] bg-[#0B1F3A]" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-5">
            <LangSwitch />
            <a
              href="mailto:Info@azroil.az"
              data-testid="header-email"
              className="text-sm text-[#0B1F3A] hover:opacity-70 transition-opacity"
            >
              Info@azroil.az
            </a>
          </div>

          <div className="md:hidden flex items-center gap-3">
            <LangSwitch testIdPrefix="mobile-" />
            <button
              className="w-10 h-10 border border-[#E5E7EB] flex items-center justify-center"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-[#E5E7EB] py-4" data-testid="mobile-menu">
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-nav-${item.key}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 py-3 text-[15px] ${
                      isActive ? "text-[#0B1F3A]" : "text-[#4B5563]"
                    }`
                  }
                >
                  <span className="mono text-[10px] text-[#9CA3AF]">{item.idx}</span>
                  {item.label}
                </NavLink>
              ))}
              <a
                href="mailto:Info@azroil.az"
                className="mt-3 text-sm text-[#0B1F3A]"
                data-testid="mobile-menu-email"
              >
                Info@azroil.az
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
